/**
 * Upload links, on the owner's side of the glass (F25).
 *
 * The owner's problem is one sentence long ("my photographer has 2,000 photos
 * and some video, how do they send them to me?") and this panel is the whole
 * answer. It lives on the Files screen rather than in Settings because that is
 * where somebody goes when they are thinking about files.
 *
 * THE URL IS SHOWN ONCE AND THE PANEL SAYS SO. The server stores only a hash,
 * so there is no route that could show it again and no amount of UI could
 * recover it. That is stated at the moment it is handed over, in the same
 * block as the value, rather than as a footnote nobody reads, and the copy
 * field stays open until the owner dismisses it themselves.
 *
 * "REVOKE" IS CALLED "TURN OFF", AND IT SAYS WHAT IT DOES NOT DO. Revoking is
 * not a delete: the files somebody already sent are the delivery, and an owner
 * who reads "revoke" next to a list of received files could reasonably fear
 * otherwise. The confirm spends a whole sentence on it.
 *
 * TWO NUMBERS, NOT ONE. What actually arrived, and (only when it is non-zero)
 * what is still on its way. Folding an in-flight reservation into "uploaded"
 * would tell the owner a delivery is complete while it is still moving.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '../primitives/Button';
import { Dialog } from '../primitives/Dialog';
import { Field } from '../primitives/Field';
import { Select } from '../primitives/Select';
import { showToast } from '../primitives/Toast';
import { CopyField } from './CopyField';
import { Notice } from './Notice';
import { SectionPanel } from './SectionPanel';
import {
  createUploadLink,
  listUploadLinks,
  revokeUploadLink,
  type UploadLink,
} from '../../lib/api';
import { captureEvent } from '../../lib/analytics/posthog';
import { useFormTracker } from '../../lib/analytics/use-form-tracker';
import { bytes as formatBytes, count } from '../../lib/format';
import { t } from '../../lib/i18n/t';

interface UploadLinkPanelProps {
  projectId: string;
  projectSlug: string;
}

const GiB = 1024 ** 3;

const DURATIONS = [
  { value: '24', label: t('1 day') },
  { value: '72', label: t('3 days') },
  { value: '168', label: t('7 days') },
];

const SIZES = [
  { value: String(10 * GiB), label: t('10 GB') },
  { value: String(50 * GiB), label: t('50 GB') },
  { value: String(200 * GiB), label: t('200 GB') },
];

function whenGone(link: UploadLink): string {
  if (link.status === 'revoked') return t('Turned off');
  if (link.status === 'expired') return t('Expired');
  const ms = new Date(link.expiresAt).getTime() - Date.now();
  if (ms <= 0) return t('Expired');
  const hours = Math.round(ms / 3_600_000);
  if (hours < 24) return t('Open for {n} more hours', { n: String(hours) });
  return t('Open for {n} more days', { n: String(Math.round(hours / 24)) });
}

export function UploadLinkPanel({ projectId, projectSlug }: UploadLinkPanelProps) {
  const [links, setLinks] = useState<UploadLink[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState<{ url: string; label: string } | null>(null);
  const [confirmOff, setConfirmOff] = useState<UploadLink | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [label, setLabel] = useState('');
  const [folder, setFolder] = useState('uploads');
  const [hours, setHours] = useState('24');
  const [budget, setBudget] = useState(String(50 * GiB));
  const [saving, setSaving] = useState(false);

  const tracker = useFormTracker({
    formId: 'upload-link-create',
    formName: 'Create upload link',
    totalFields: 4,
    projectSlug,
  });

  const refresh = useCallback(async () => {
    try {
      const { links: next } = await listUploadLinks(projectId);
      setLinks(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Could not load your upload links.'));
      setLinks([]);
    }
  }, [projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const active = useMemo(() => (links ?? []).filter((l) => l.status === 'active'), [links]);

  async function submit() {
    if (label.trim().length === 0) {
      tracker.onValidationError('label', 'required');
      setError(t('Give the link a name so the person opening it knows what it is for.'));
      return;
    }
    tracker.trackSubmit({ folder, hours, budget });
    setSaving(true);
    setError(null);
    try {
      const created = await createUploadLink(projectId, {
        label: label.trim(),
        prefix: folder.trim() || 'uploads',
        expiresInHours: Number(hours),
        maxBytes: Number(budget),
      });
      tracker.trackSubmitSuccess({ link_id: created.id });
      captureEvent('upload_link_created', {
        project_slug: projectSlug,
        expires_in_hours: Number(hours),
        max_bytes: Number(budget),
      });
      setIssued({ url: created.url, label: created.label });
      setCreating(false);
      setLabel('');
      await refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('Could not create the link.');
      tracker.trackSubmitError('create_failed', message);
      setError(message);
    } finally {
      setSaving(false);
    }
  }

  async function turnOff(link: UploadLink) {
    setConfirmOff(null);
    try {
      await revokeUploadLink(projectId, link.id);
      captureEvent('upload_link_revoked', { project_slug: projectSlug });
      showToast(t('Upload link turned off.'), link.label);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Could not turn off the link.'));
    }
  }

  return (
    <SectionPanel
      title={t('Upload links')}
      note={t('Send a link to someone outside your team, a photographer or a client, and they can upload straight into your files. They need no account.')}
      action={
        <Button
          variant="outline"
          onClick={() => {
            setCreating(true);
            captureEvent('upload_link_form_opened', { project_slug: projectSlug });
          }}
        >
          {t('Create a link')}
        </Button>
      }
    >
      {error ? (
        <Notice tone="attention" title={t('Something went wrong')}>
          {error}
        </Notice>
      ) : null}

      {links === null ? (
        <p className="ulink__empty">{t('Loading…')}</p>
      ) : active.length === 0 ? (
        <p className="ulink__empty">
          {t('No upload links yet. Create one when somebody needs to send you files.')}
        </p>
      ) : (
        <ul className="ulink__list">
          {active.map((link) => (
            <li className="ulink__row" key={link.id}>
              <div className="ulink__main">
                <span className="ulink__label">{link.label}</span>
                <span className="ulink__meta mono">{link.prefix}</span>
              </div>
              <div className="ulink__stats">
                <span>
                  {link.uploadedFiles === 0
                    ? t('Nothing sent yet')
                    : t('{n} files · {size}', {
                        n: count(link.uploadedFiles),
                        size: formatBytes(link.uploadedBytes),
                      })}
                </span>
                {/* Only when it is non-zero: a reservation of 0 bytes is not a
                    fact worth a line, and "0 on the way" reads as an error. */}
                {link.reservedBytes > 0 ? (
                  <span className="ulink__inflight">
                    {t('{size} on the way', { size: formatBytes(link.reservedBytes) })}
                  </span>
                ) : null}
                <span className="ulink__when">{whenGone(link)}</span>
              </div>
              <Button variant="ghost" onClick={() => setConfirmOff(link)}>
                {t('Turn off')}
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={creating}
        onClose={() => {
          setCreating(false);
          tracker.trackCancel('dismissed');
        }}
        title={t('Create an upload link')}
        description={t('Anyone with the link can send you files until it expires. They cannot see or download anything you already have.')}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setCreating(false);
                tracker.trackCancel('cancelled');
              }}
            >
              {t('Cancel')}
            </Button>
            <Button variant="solid" onClick={() => void submit()} disabled={saving}>
              {saving ? t('Creating…') : t('Create link')}
            </Button>
          </>
        }
      >
        <Field
          label={t('What is this for?')}
          help={t('The person opening the link sees this. For example: Wedding photos, August.')}
          value={label}
          onFocus={() => tracker.onFieldFocus('label')}
          onBlur={() => tracker.onFieldBlur('label')}
          onChange={(e) => {
            setLabel(e.target.value);
            tracker.onFieldChange('label');
          }}
        />
        <Field
          label={t('Folder')}
          mono
          help={t('Where the files land in your storage.')}
          value={folder}
          onFocus={() => tracker.onFieldFocus('folder')}
          onBlur={() => tracker.onFieldBlur('folder')}
          onChange={(e) => {
            setFolder(e.target.value);
            tracker.onFieldChange('folder');
          }}
        />
        <Select
          label={t('How long should it work?')}
          value={hours}
          options={DURATIONS}
          onChange={(e) => {
            setHours(e.target.value);
            tracker.onFieldChange('hours');
          }}
        />
        <Select
          label={t('How much can they send?')}
          value={budget}
          options={SIZES}
          onChange={(e) => {
            setBudget(e.target.value);
            tracker.onFieldChange('budget');
          }}
        />
      </Dialog>

      {/* Shown once. There is no route that could produce it a second time. */}
      <Dialog
        open={issued !== null}
        onClose={() => setIssued(null)}
        title={t('Send this link')}
        description={t('This is the only time it is shown. If you lose it, create another one. This one keeps working until it expires.')}
        footer={
          <Button variant="solid" onClick={() => setIssued(null)}>
            {t('Done')}
          </Button>
        }
      >
        {issued ? <CopyField label={t('Upload link')} value={issued.url} /> : null}
      </Dialog>

      <Dialog
        open={confirmOff !== null}
        onClose={() => setConfirmOff(null)}
        title={t('Turn off this link?')}
        description={t('Nobody will be able to send anything new with it. The files already uploaded stay exactly where they are. This does not delete a delivery.')}
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmOff(null)}>
              {t('Keep it on')}
            </Button>
            <Button
              variant="danger-outline"
              onClick={() => confirmOff && void turnOff(confirmOff)}
            >
              {t('Turn it off')}
            </Button>
          </>
        }
      />
    </SectionPanel>
  );
}
